<template>
  <div class="teacher-management">
    <el-row :gutter="20">
      <!-- Subject Management -->
      <el-col :span="8">
        <el-card class="subject-card">
          <template #header>
            <div class="subject-header" style="display: flex; justify-content: space-between; align-items: center;">
              <span>科目管理</span>
              <div style="display: flex; gap: 8px;">
                <el-dropdown trigger="click" @command="handleImportPreset">
                  <el-button type="warning" size="small" plain>导入预设科目</el-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item command="primary">小学常用科目</el-dropdown-item>
                      <el-dropdown-item command="junior">初中常用科目</el-dropdown-item>
                      <el-dropdown-item command="senior">高中常用科目</el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
                <el-button type="primary" size="small" @click="showSubjectDialog">新建科目</el-button>
              </div>
            </div>
          </template>
          <el-table :data="subjects" v-loading="loadingSubjects" size="small">
            <el-table-column prop="name" label="科目名称" />
            <el-table-column label="操作" width="120">
              <template #default="{ row }">
                <el-button text type="primary" size="small" @click="editSubject(row)">编辑</el-button>
                <el-popconfirm title="确定删除？" @confirm="deleteSubject(row.id)">
                  <template #reference>
                    <el-button text type="danger" size="small">删除</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>

      <!-- Teacher List -->
      <el-col :span="16">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>教师列表 <small style="font-size: 13px; color: #6b7280; font-weight: normal; margin-left: 4px;">(共 {{ teachers.length }} 人)</small></span>
              <div class="header-actions">
                <el-button @click="showBatchImportDialog">
                  <el-icon>
                    <MagicStick />
                  </el-icon>
                  批量导入
                </el-button>
                <el-button type="warning" :disabled="selectedTeachers.length === 0" @click="showBatchGradesDialog" style="margin-right: 12px;">
                  批量设置年级 (已选 {{ selectedTeachers.length }} 人)
                </el-button>
                <el-popconfirm title="确定要删除选中的教师吗？此操作不可恢复" @confirm="handleBatchDelete" width="260">
                  <template #reference>
                    <el-button type="danger" :disabled="selectedTeachers.length === 0" style="margin-right: 12px;">
                      <el-icon><Delete /></el-icon>
                      批量删除
                    </el-button>
                  </template>
                </el-popconfirm>
                <el-button type="primary" @click="showTeacherDialog">
                  <el-icon>
                    <Plus />
                  </el-icon>
                  新建教师
                </el-button>
              </div>
            </div>
          </template>

          <el-table :data="teachers" v-loading="loadingTeachers" stripe @selection-change="handleSelectionChange">
            <el-table-column type="selection" width="55" />
            <el-table-column prop="name" label="姓名" width="120" />
            <el-table-column label="任教科目" min-width="150">
              <template #default="{ row }">
                <el-tag v-for="subject in row.subjects" :key="subject.id" size="small" style="margin-right: 4px">
                  {{ subject.name }}
                </el-tag>
                <span v-if="!row.subjects?.length" style="color: #6b7280">-</span>
              </template>
            </el-table-column>
            <el-table-column label="任教年级" width="150">
              <template #default="{ row }">
                <el-tag v-for="(grade, idx) in row.grades" :key="idx" type="info" size="small"
                  style="margin-right: 4px">
                  {{ grade }}
                </el-tag>
                <span v-if="!row.grades?.length" style="color: #6b7280">-</span>
              </template>
            </el-table-column>
            <el-table-column label="最大监考" width="100">
              <template #default="{ row }">
                <el-tag v-if="row.max_hours" type="warning" size="small">
                  {{ isMinutes ? (row.max_hours * 60).toFixed(0) + '分钟' : row.max_hours + 'h' }}
                </el-tag>
                <span v-else style="color: #9ca3af">无限制</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120" fixed="right">
              <template #default="{ row }">
                <el-button text type="primary" @click="editTeacher(row)">编辑</el-button>
                <el-popconfirm title="确定删除？" @confirm="deleteTeacher(row.id)">
                  <template #reference>
                    <el-button text type="danger">删除</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <!-- Subject Dialog -->
    <el-dialog v-model="subjectDialogVisible" :title="isEditSubject ? '编辑科目' : '新建科目'" width="440px">
      <el-form :model="subjectForm" label-width="80px">
        <el-form-item label="科目名称">
          <el-input v-model="subjectForm.name" placeholder="如：语文、数学" />
        </el-form-item>
        <div style="margin-left: 80px; margin-top: 10px;">
          <el-alert
            title="提示：如果是高中“3+1+2”或“4选2”同一时间开考的多学科组合考试，您可以创建“选科综合(4选2)”或“选科(物理/历史/化学/生物)”作为科目，以便统一排考。"
            type="info"
            :closable="false"
            show-icon
            style="line-height: 1.4;"
          />
        </div>
      </el-form>
      <template #footer>
        <el-button @click="subjectDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitSubject" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Teacher Dialog -->
    <el-dialog v-model="teacherDialogVisible" :title="isEdit ? '编辑教师' : '新建教师'" width="550px">
      <el-form :model="teacherForm" label-width="100px" :rules="teacherRules" ref="teacherFormRef">
        <el-form-item label="工号" prop="employee_id">
          <el-input v-model="teacherForm.employee_id" placeholder="教师工号" />
        </el-form-item>
        <el-form-item label="姓名" prop="name">
          <el-input v-model="teacherForm.name" placeholder="教师姓名" />
        </el-form-item>
        <el-form-item label="任教科目">
          <el-select v-model="teacherForm.subject_ids" multiple placeholder="选择任教科目" style="width: 100%">
            <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="任教年级">
          <el-select v-model="teacherForm.grades" multiple allow-create filterable placeholder="选择或输入年级"
            style="width: 100%">
            <el-option v-for="g in semesterGrades" :key="g" :label="g" :value="g" />
          </el-select>
        </el-form-item>
        <el-form-item label="最大监考时长">
          <el-input-number v-model="teacherForm.max_hours" :min="0" :step="isMinutes ? 10 : 0.5" :precision="isMinutes ? 0 : 1" placeholder="留空表示不限制" />
          <span style="margin-left: 8px; color: #6b7280">{{ isMinutes ? '分钟/学期' : '小时/学期' }}，留空表示不限制</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="teacherDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTeacher" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Batch Import Dialog -->
    <el-dialog v-model="batchImportDialogVisible" title="批量导入教师" width="900px">
      <el-tabs v-model="importTab" class="import-tabs">
        <el-tab-pane label="Excel导入" name="excel">
          <div class="excel-import-section">
            <div class="import-instructions">
              <p>请下载标准的 Excel 模板，按照要求填写信息后上传。</p>
              <p><strong>注意：</strong>姓名和学科为必填项。</p>
              <el-button type="primary" plain @click="downloadTeacherTemplate" style="margin-top: 10px;">
                <el-icon><Download /></el-icon> 下载 Excel 模板
              </el-button>
            </div>
            
            <el-upload
              ref="teacherUploadRef"
              class="upload-demo"
              drag
              action=""
              :auto-upload="false"
              :on-change="handleTeacherExcelChange"
              :on-remove="handleTeacherExcelRemove"
              :limit="1"
              accept=".xlsx, .xls"
            >
              <el-icon class="el-icon--upload"><upload-filled /></el-icon>
              <div class="el-upload__text">
                将 Excel 文件拖到此处，或 <em>点击上传</em>
              </div>
              <template #tip>
                <div class="el-upload__tip">
                  只能上传 xlsx / xls 文件
                </div>
              </template>
            </el-upload>
          </div>
        </el-tab-pane>

        <el-tab-pane label="粘贴文本" name="text">
          <div class="batch-import-container">
            <div class="import-instructions">
              <p>请粘贴教师信息，每行一个，格式：<strong>姓名 任教科目 任教年级</strong>（支持空格、制表符、逗号或分号分隔，支持中英文符号）</p>
              <p>年级或多字段分隔符支持中文/英文逗号、顿号、斜杠、空格或竖线。例如：<strong>高一,高二</strong>、<strong>高一/高二</strong>、<strong>高一、高二</strong></p>
              <p class="example">示例：<br />张三，语文，高一,高二<br />李四 英语 高三<br />王五 数学 高一、高二</p>
            </div>

            <el-input v-model="importText" type="textarea" :rows="8" placeholder="在此粘贴教师信息..." @input="parseImportText" />

            <div v-if="parsedTeachers.length > 0" class="preview-section">
              <h4>解析预览 ({{ parsedTeachers.length }} 位教师)</h4>
              <el-table :data="parsedTeachers" size="small" border height="300">
                <el-table-column prop="name" label="姓名" width="100" />
                <el-table-column prop="subject_name" label="科目" width="100" />
                <el-table-column label="年级">
                  <template #default="{ row }">
                    <el-tag v-for="g in row.grades" :key="g" size="small" style="margin-right: 4px">{{ g }}</el-tag>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <template #footer>
        <el-button @click="batchImportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchImport" :loading="submitting"
          :disabled="(importTab === 'text' && parsedTeachers.length === 0) || (importTab === 'excel' && !teacherFile)">
          确定导入
        </el-button>
      </template>
    </el-dialog>

    <!-- Batch Update Grades Dialog -->
    <el-dialog v-model="batchGradesDialogVisible" title="批量设置任教年级" width="450px">
      <div style="margin-bottom: 16px;">
        已选择 <strong>{{ selectedTeachers.length }}</strong> 位教师，将把他们的任教年级统一设置为：
      </div>
      <el-form label-width="80px">
        <el-form-item label="任教年级">
          <el-select v-model="batchGradesForm.grades" multiple placeholder="选择要设置的年级" style="width: 100%">
            <el-option v-for="g in semesterGrades" :key="g" :label="g" :value="g" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="batchGradesDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchGrades" :loading="submitting" :disabled="batchGradesForm.grades.length === 0">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Plus, MagicStick, Download, UploadFilled, Delete } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { teacherApi, semesterApi } from '@/api'
import { getUser } from '@/utils/auth'

const isMinutes = computed(() => getUser()?.use_minutes_format || false)

const subjects = ref([])
const teachers = ref([])
const semesterGrades = ref([])
const loadingSubjects = ref(false)
const loadingTeachers = ref(false)
const submitting = ref(false)

const subjectDialogVisible = ref(false)
const teacherDialogVisible = ref(false)
const batchImportDialogVisible = ref(false)
const batchGradesDialogVisible = ref(false)
const isEdit = ref(false)
const editingId = ref(null)
const teacherFormRef = ref(null)
const selectedTeachers = ref([])
const batchGradesForm = ref({
  grades: []
})

const importTab = ref('excel')
const teacherUploadRef = ref(null)
const teacherFile = ref(null)
const importText = ref('')
const parsedTeachers = ref([])

const subjectForm = ref({ name: '' })
const teacherForm = ref({
  employee_id: '',
  name: '',
  subject_ids: [],
  grades: [],
  max_hours: null
})

const teacherRules = {
  name: [{ required: true, message: '请输入姓名', trigger: 'blur' }]
}

const fetchSubjects = async () => {
  loadingSubjects.value = true
  try {
    subjects.value = await teacherApi.getSubjects()
  } catch (e) {
    console.error(e)
  } finally {
    loadingSubjects.value = false
  }
}

const fetchTeachers = async () => {
  loadingTeachers.value = true
  try {
    teachers.value = await teacherApi.getAll()
  } catch (e) {
    console.error(e)
  } finally {
    loadingTeachers.value = false
  }
}

const handleSelectionChange = (selection) => {
  selectedTeachers.value = selection
}

const showBatchGradesDialog = () => {
  batchGradesForm.value.grades = []
  batchGradesDialogVisible.value = true
}

const submitBatchGrades = async () => {
  if (selectedTeachers.value.length === 0) return
  submitting.value = true
  try {
    await teacherApi.batchUpdateGrades({
      teacher_ids: selectedTeachers.value.map(t => t.id),
      grades: batchGradesForm.value.grades
    })
    ElMessage.success('批量设置年级成功')
    batchGradesDialogVisible.value = false
    fetchTeachers()
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const handleBatchDelete = async () => {
  if (selectedTeachers.value.length === 0) return
  submitting.value = true
  try {
    await teacherApi.batchDelete({
      teacher_ids: selectedTeachers.value.map(t => t.id)
    })
    ElMessage.success('批量删除成功')
    selectedTeachers.value = []
    fetchTeachers()
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const isEditSubject = ref(false)
const editingSubjectId = ref(null)

const showSubjectDialog = () => {
  isEditSubject.value = false
  editingSubjectId.value = null
  subjectForm.value = { name: '' }
  subjectDialogVisible.value = true
}

const editSubject = (row) => {
  isEditSubject.value = true
  editingSubjectId.value = row.id
  subjectForm.value = { name: row.name }
  subjectDialogVisible.value = true
}

const submitSubject = async () => {
  if (!subjectForm.value.name) {
    ElMessage.warning('请输入科目名称')
    return
  }
  submitting.value = true
  try {
    if (isEditSubject.value) {
      await teacherApi.updateSubject(editingSubjectId.value, subjectForm.value)
      ElMessage.success('科目已更新')
    } else {
      await teacherApi.createSubject(subjectForm.value)
      ElMessage.success('科目创建成功')
    }
    subjectDialogVisible.value = false
    fetchSubjects()
    fetchTeachers() // Refresh teacher subjects display names
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const handleImportPreset = async (presetType) => {
  try {
    const res = await teacherApi.importPresetSubjects(presetType)
    ElMessage.success(res.message || '导入成功')
    fetchSubjects()
  } catch (e) {
    console.error(e)
    ElMessage.error('导入预设科目失败')
  }
}

const deleteSubject = async (id) => {
  try {
    await teacherApi.deleteSubject(id)
    ElMessage.success('科目已删除')
    fetchSubjects()
  } catch (e) {
    console.error(e)
  }
}

const showTeacherDialog = () => {
  isEdit.value = false
  editingId.value = null
  teacherForm.value = {
    employee_id: '',
    name: '',
    subject_ids: [],
    grades: [],
    max_hours: null
  }
  teacherDialogVisible.value = true
}

const editTeacher = (row) => {
  isEdit.value = true
  editingId.value = row.id
  teacherForm.value = {
    employee_id: row.employee_id,
    name: row.name,
    subject_ids: row.subjects?.map(s => s.id) || [],
    grades: row.grades || [],
    max_hours: row.max_hours ? (isMinutes.value ? Math.round(row.max_hours * 60) : row.max_hours) : null
  }
  teacherDialogVisible.value = true
}

const submitTeacher = async () => {
  if (!teacherFormRef.value) return
  await teacherFormRef.value.validate(async (valid) => {
    if (!valid) return

    submitting.value = true
    try {
      const payload = {
        ...teacherForm.value,
        max_hours: teacherForm.value.max_hours ? (isMinutes.value ? teacherForm.value.max_hours / 60 : teacherForm.value.max_hours) : null
      }
      if (isEdit.value) {
        await teacherApi.update(editingId.value, payload)
        ElMessage.success('教师信息已更新')
      } else {
        await teacherApi.create(payload)
        ElMessage.success('教师创建成功')
      }
      teacherDialogVisible.value = false
      fetchTeachers()
    } catch (e) {
      console.error(e)
    } finally {
      submitting.value = false
    }
  })
}

const deleteTeacher = async (id) => {
  try {
    await teacherApi.delete(id)
    ElMessage.success('教师已删除')
    fetchTeachers()
  } catch (e) {
    console.error(e)
  }
}

const showBatchImportDialog = () => {
  importTab.value = 'excel'
  teacherFile.value = null
  if (teacherUploadRef.value) {
    teacherUploadRef.value.clearFiles()
  }
  importText.value = ''
  parsedTeachers.value = []
  batchImportDialogVisible.value = true
}

const handleTeacherExcelChange = (uploadFile) => {
  teacherFile.value = uploadFile.raw
}

const handleTeacherExcelRemove = () => {
  teacherFile.value = null
}

const downloadTeacherTemplate = async () => {
  try {
    const blob = await teacherApi.downloadTemplate()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '教师导入模板.xlsx'
    link.click()
    window.URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
    ElMessage.error('下载模板失败')
  }
}

const parseImportText = () => {
  if (!importText.value.trim()) {
    parsedTeachers.value = []
    return
  }

  const lines = importText.value.split('\n')
  const result = []

  lines.forEach(line => {
    const rawLine = line.trim()
    if (!rawLine) return

    // 1. Smartly detect field separator
    let parts = []
    if (rawLine.includes('\t')) {
      parts = rawLine.split('\t')
    } else {
      const englishCommas = (rawLine.match(/,/g) || []).length
      const chineseCommas = (rawLine.match(/，/g) || []).length
      const totalCommas = englishCommas + chineseCommas
      
      const englishSemicolons = (rawLine.match(/;/g) || []).length
      const chineseSemicolons = (rawLine.match(/；/g) || []).length
      const totalSemicolons = englishSemicolons + chineseSemicolons

      const enumerationCommas = (rawLine.match(/、/g) || []).length
      const verticalBars = (rawLine.match(/\|/g) || []).length

      if (totalCommas >= 2) {
        parts = rawLine.split(/[,，]/)
      } else if (totalSemicolons >= 2) {
        parts = rawLine.split(/[;；]/)
      } else if (enumerationCommas >= 2) {
        parts = rawLine.split(/[、]/)
      } else if (verticalBars >= 2) {
        parts = rawLine.split(/[|]/)
      } else {
        parts = rawLine.split(/\s+/)
      }
    }

    // Clean up parts
    parts = parts.map(p => p.trim()).filter(p => p.length > 0)

    if (parts.length >= 2) {
      const name = parts[0]
      const subject_name = parts[1]
      
      const gradeStartIndex = 2
      const remainingParts = parts.slice(gradeStartIndex)
      
      const grades = []
      remainingParts.forEach(part => {
        const subGrades = part.split(/[\s,，/、|]+/).filter(g => g.trim().length > 0)
        grades.push(...subGrades)
      })

      const uniqueGrades = Array.from(new Set(grades))

      if (name && subject_name) {
        result.push({
          name,
          subject_name,
          grades: uniqueGrades
        })
      }
    }
  })

  parsedTeachers.value = result
}

const submitBatchImport = async () => {
  if (importTab.value === 'text') {
    if (parsedTeachers.value.length === 0) return

    submitting.value = true
    try {
      const res = await teacherApi.batchImport({ teachers: parsedTeachers.value })
      ElMessage.success(res.message || '导入成功')
      batchImportDialogVisible.value = false
      fetchTeachers()
      fetchSubjects()
    } catch (e) {
      console.error(e)
    } finally {
      submitting.value = false
    }
  } else if (importTab.value === 'excel') {
    if (!teacherFile.value) {
      ElMessage.warning('请先选择要上传的 Excel 文件')
      return
    }

    submitting.value = true
    try {
      const res = await teacherApi.batchImportExcel(teacherFile.value)
      ElMessage.success(res.message || '导入成功')
      batchImportDialogVisible.value = false
      fetchTeachers()
      fetchSubjects()
    } catch (e) {
      console.error(e)
    } finally {
      submitting.value = false
    }
  }
}

onMounted(async () => {
  fetchSubjects()
  fetchTeachers()
  try {
    const semesters = await semesterApi.getAll()
    const active = semesters.find(s => s.is_active)
    const sem = active || semesters[0]
    if (sem && sem.available_grades) {
      semesterGrades.value = sem.available_grades.split(',').map(g => g.trim()).filter(g => g.length > 0)
    } else {
      semesterGrades.value = []
    }
  } catch (e) {
    console.error(e)
    semesterGrades.value = []
  }
})
</script>

<style scoped>
.teacher-management {
  max-width: 1400px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 16px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.batch-import-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.import-instructions {
  background-color: #f8fafc;
  padding: 12px;
  border-radius: 6px;
  font-size: 14px;
  color: #475569;
}

.import-instructions p {
  margin: 4px 0;
}

.import-instructions .example {
  color: #94a3b8;
  font-family: monospace;
  margin-top: 8px;
}

.preview-section h4 {
  margin: 0 0 12px 0;
  color: #1e293b;
}
</style>
